import { usePrevious } from '@apia/util';
import { useModal } from '../../../components/modals/hooks/useModal';
import { Modal, TModal } from '../../../components/modals/Modal';
import { Confirm, IConfirm } from '../../../components';
import React, { useRef } from 'react';
import { Box } from '@apia/theme';

export type TOpenModal = Pick<
  TModal,
  | 'NavBar'
  | 'Portal'
  | 'children'
  | 'className'
  | 'hideCloseButton'
  | 'initialFocusGetter'
  | 'initialFocusConfiguration'
  | 'maxWidth'
  | 'noFocus'
  | 'noHeader'
  | 'onExited'
  | 'shouldCloseOnEsc'
  | 'shouldCloseOnOverlayClick'
  | 'size'
  | 'stretch'
  | 'title'
  | 'variant'
  | 'draggable'
  | 'onClose'
  | 'onDragStart'
  | 'defaultPosition'
  | 'dragBounded'
> & {
  id?: string;
  children?: React.ReactNode;
  confirmProps?: Omit<IConfirm, 'onConfirmOk' | 'onConfirmCancel' | 'children'>;
  /**
   * Determina si el modal debe abrirse al ser montado o no.
   */
  isDefaultOpen?: boolean;
  /**
   * El método onConfirm permite pasar un callback que devuelva una promesa. En
   * caso de ser así, si esta promesa resuelve a true, el modal será cerrado o
   * en caso contrario permanecerá abierto. Es útil en situaciones en donde es
   * necesario corroborar una condición antes de cerrar el modal. En la
   * situación en que se decida mantener el modal abierto, está a cargo del
   * usuario del componente realizar las indicaciones visuales correspondientes
   * para indicar al usuario el motivo por el cual el modal no fue cerrado.
   */
  onConfirm?: () => Promise<boolean> | boolean | void;
  /**
   * El método onCancel será llamado siempre que un usuario cierre el modal,
   * excepto cuando haya presionado en onConfirm.
   */
  onCancel?: () => void;
  /**
   * Permite introducir html directamente dentro del contenedor del modal.
   */
  html?: string;
};

export const OpenModal = (props: TOpenModal & { isOpen: boolean }) => {
  const modalProps = useModal({
    isDefaultOpen: true,
    onExited: props.onExited,
    onClose: props.onClose,
  });

  const previousOpenProp = usePrevious(props.isOpen);
  if (previousOpenProp.current !== props.isOpen && !props.isOpen) {
    modalProps.hide();
  }

  const hasConfirmed = useRef(false);

  const confirmProps = props.confirmProps;
  if (props.onConfirm || props.onCancel) {
    return (
      <Modal
        {...props}
        {...modalProps}
        onExited={() => {
          props.onClose?.();
          props.onExited?.();
          if (!hasConfirmed.current) {
            props.onCancel?.();
          }
        }}
      >
        <Confirm
          {...confirmProps}
          onConfirm={async () => {
            hasConfirmed.current = true;
            const res = await props.onConfirm?.();

            if (res !== false) {
              modalProps.hide();
            }
          }}
          onCancel={() => {
            modalProps.hide();
          }}
          children={
            props.html ? (
              <Box dangerouslySetInnerHTML={{ __html: props.html }} />
            ) : (
              props.children
            )
          }
        />
      </Modal>
    );
  }

  return (
    <Modal
      {...props}
      {...modalProps}
      children={
        props.html ? (
          <Box dangerouslySetInnerHTML={{ __html: props.html }} />
        ) : (
          props.children
        )
      }
    />
  );
};
